import React, { useCallback, useMemo, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import dayjs from 'dayjs'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Button,
  CopyButton,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  TextMask,
} from '@node-space/ui-components'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ApiKey } from 'types/apiKeys'
import { ActionIds } from 'types/types'
import { getMaskedText } from 'utils/string'
import { editApiKeyDataAtom } from '../atoms/editApiKeyDataAtom'
import { ApiKeySheet } from './ApiKeySheet/ApiKeySheet'
import ApiKeyDeleteModal from './Modal/ApiKeyDeleteModal'

export const formattedDate = (date: string) => {
  const formattedDate = dayjs(date).format('DD MMMM YYYY')
  return formattedDate
}

type ApiKeysTableProps = {
  apiKeys: ApiKey[]
  from2FA: boolean
}

const ApiKeysTable = ({ apiKeys, from2FA }: ApiKeysTableProps) => {
  const { t } = useTranslation()
  const permissions = useMappedRoles().manageApiIntegration
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [apiKeySheetOpen, setApiKeySheetOpen] = useState(false)

  const setApiKeyAction = useSetAtom(actionAtom)
  const setEditApiKeyData = useSetAtom(editApiKeyDataAtom)

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return [
      {
        key: 'name',
        label: t('name'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'hawkAuthId',
        label: t('hawkAuthID'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'additionallyEnabledFor',
        label: t('manageApiKeys.apiKeysEnabledFor'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'activeSince',
        label: t('manageApiKeys.apiKeysActiveSince'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ]
  }, [])

  const onDeleteClicked = useCallback(
    (apiKey: ApiKey) => {
      setSelectedApiKey(apiKey)
      setShowDeleteModal(true)
    },
    [setSelectedApiKey, setShowDeleteModal]
  )

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    if (!!selectedApiKey) {
      setSelectedApiKey(null)
    }
  }

  const handleCopyApiKey = (apiKey: string) => {
    if (apiKey) navigator?.clipboard?.writeText(apiKey)
  }

  const handleOpenApiKeyManageSheet = (apiKey: ApiKey) => {
    setApiKeySheetOpen(true)
    setEditApiKeyData(apiKey)
    setApiKeyAction({
      actionId: ActionIds.API_KEY_MANAGE,
      header: t('manageApiKeys.manageApiKeyModalHeader'),
      onClose: () => {
        setApiKeySheetOpen(false)
      },
    })
  }

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!apiKeys) return []

    return apiKeys?.map(apiKey => {
      return {
        id: apiKey?.id?.toString(),
        testid: apiKey?.id?.toString(),
        cells: [
          {
            key: 'name',
            variant: DataTableCellVariants.TEXT,
            Text: apiKey?.description,
          },
          {
            key: 'hawkAuthId',
            variant: DataTableCellVariants.TEXT,
            Text: <TextMask tooltipText={apiKey?.tokenValue}>{getMaskedText(20)}</TextMask>,
            Actions: [
              <CopyButton
                noPadding={false}
                key={`copy-${apiKey?.tokenValue}`}
                copyText={apiKey?.tokenValue}
              />,
            ],
          },
          {
            key: 'additionallyEnabledFor',
            variant: DataTableCellVariants.TEXT,
            Text: apiKey?.canWithdraw ? 'Withdrawals' : '',
          },
          {
            key: 'activeSince',
            variant: DataTableCellVariants.TEXT,
            Text: formattedDate(apiKey?.dateCreated),
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: permissions?.canEdit
              ? [
                  <DropdownMenu
                    key={`context-menu-${apiKey.id}`}
                    DropdownMenuItems={[
                      <DropdownMenuItem
                        key="edit"
                        LeadingIcon={<Icon name="SimplePencilCustom" />}
                        onClick={() => handleOpenApiKeyManageSheet(apiKey)}
                      >
                        {t('apiManage.actionButtons.editBtn')}
                      </DropdownMenuItem>,
                      <DropdownMenuItem
                        key="delete"
                        LeadingIcon={<Icon name="Trash" />}
                        onClick={() => onDeleteClicked(apiKey)}
                      >
                        {t('apiManage.actionButtons.deleteBtn')}
                      </DropdownMenuItem>,
                    ]}
                  >
                    <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
                  </DropdownMenu>,
                ]
              : [],
          },
        ],
      }
    })
  }, [apiKeys])

  return (
    <>
      {selectedApiKey && showDeleteModal && (
        <ApiKeyDeleteModal
          selectedApiKey={selectedApiKey}
          handleCloseModal={handleCloseDeleteModal}
        />
      )}

      <ApiKeySheet isSheetOpen={apiKeySheetOpen} from2FA={from2FA} />
      {apiKeys && apiKeys.length > 0 && <DataTable headers={columns} data={tableData} />}
    </>
  )
}

export default React.memo(ApiKeysTable)
